/**
 * Identity Authentication Client
 * Handles authentication with Welbee Identity App via HTTP-only cookies
 * Cross-domain session management for all Welbee applications
 */

import { getIdentityUrl as identityUrlForCurrentHost } from "../utils/Config";


const REDIRECT_STORAGE_KEY = 'welbee_last_identity_redirect_ts';
const MIN_REDIRECT_INTERVAL_MS = 5 * 1000; // 5 seconds
const QUICK_RETRY_DELAY_MS = 2 * 1000;
const ERROR_BACKOFF_MS = 15 * 1000;
// Identity refreshes the session server-side once it is within 5 minutes of expiry, so the poll
// has to land inside that window or the token simply runs out between checks.
const SESSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const TOKEN_REFRESH_LEEWAY_MS = 30 * 1000;
const WAKE_RECHECK_THROTTLE_MS = 30 * 1000;
// Every caller - polling, wake-ups and each API request's token refresh - joins the one in-flight
// check, so a check that never settles would stall all of them. It must always finish.
const SESSION_CHECK_TIMEOUT_MS = 10 * 1000;
// A single 401 can be a race with the server-side refresh. Only a 401 arriving at least this long
// after the first one - i.e. from a genuinely separate check, not a burst of concurrent API
// renewals - confirms the session is really gone.
const SIGN_OUT_CONFIRMATION_DELAY_MS = QUICK_RETRY_DELAY_MS;
// Identity ends a session after 60 minutes without real user activity in any Welbee app. Each
// token request reports how long this tab has been idle; background polling is not activity.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
// Captured on window so interaction inside any element (including scrolling panes) counts.
const ACTIVITY_LISTENER_OPTIONS = { capture: true, passive: true };
const ACTIVITY_RECORD_THROTTLE_MS = 5 * 1000;
// An active user's activity reaches Identity within this long, however rarely polling runs.
const ACTIVITY_HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

const hasDom = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * The URL with `idleMs` set, keeping any query string it already carries.
 */
export const withIdleMs = (url, idleMs) => {
  const parsed = new URL(url, hasDom() ? window.location.href : undefined);
  parsed.searchParams.set('idleMs', String(idleMs));
  return parsed.toString();
};

export const SessionStatus = Object.freeze({
  Authenticated: 'authenticated',
  SignedOut: 'signedOut',
  // Identity could not vouch for the session right now (outage, network, unconfirmed 401).
  Unverified: 'unverified',
});

class IdentityAuth {
  constructor() {
    this.jwtToken = null;
    this.expiresAt = 0;
    this.sessionCheckInterval = null;
    this.pendingRetryTimeout = null;
    this.pendingRedirectTimeout = null;
    this.inFlightCheck = null;
    this.lastCheckStartedAt = 0;
    this.firstUnauthorizedAt = 0;
    this.backoffUntil = 0;
    this.loggedOut = false;
    // Loading or reloading a page is itself activity.
    this.lastInteractionAt = Date.now();
    this.lastActivityReportAt = 0;
    this.lastHeartbeatAt = 0;
  }

  /**
   * Get the Identity URL from config
   * This ensures we always get the current value, not a cached one
   */
  getIdentityUrl() {
    return identityUrlForCurrentHost();
  }

  /**
   * Check authentication status with Identity app.
   * Resolves true only when Identity vouches for the session (or, while Identity is unreachable,
   * the last token it issued is still valid).
   */
  async checkAuth() {
    return (await this.checkSession()) === SessionStatus.Authenticated;
  }

  /**
   * Ask Identity for the current session. Concurrent callers share one request.
   * @returns {Promise<string>} one of SessionStatus
   */
  checkSession() {
    if (this.loggedOut) {
      return Promise.resolve(SessionStatus.SignedOut);
    }

    this.inFlightCheck ??= this.fetchSession().finally(() => {
      this.inFlightCheck = null;
    });
    return this.inFlightCheck;
  }

  async fetchSession() {
    if (Date.now() < this.backoffUntil) {
      return this.statusFromCachedToken();
    }

    this.lastCheckStartedAt = Date.now();
    const request = new AbortController();
    let deadline;
    const timedOut = new Promise((_, reject) => {
      deadline = setTimeout(() => {
        request.abort();
        reject(new Error('session check timed out'));
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    try {
      return await Promise.race([this.requestSession(request.signal), timedOut]);
    } catch (error) {
      return this.recordTransientFailure(error);
    } finally {
      clearTimeout(deadline);
    }
  }

  async requestSession(signal) {
    const tokenUrl = withIdleMs(
      `${this.getIdentityUrl()}/api/auth/token`,
      this.idleMs()
    );
    const res = await fetch(tokenUrl, {
      credentials: 'include',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      },
      signal,
    });

    if (this.loggedOut) {
      return SessionStatus.SignedOut;
    }
    if (res.status === 401) {
      return this.recordUnauthorized();
    }
    if (!res.ok) {
      return this.recordTransientFailure(`status ${res.status}`);
    }

    const { jwtToken, expiresAt } = await res.json();
    return this.recordSession(jwtToken, expiresAt);
  }

  recordSession(jwtToken, expiresAt) {
    // The body can still be arriving when the user logs out; it must not revive the session.
    if (this.loggedOut) {
      return SessionStatus.SignedOut;
    }

    this.jwtToken = jwtToken;
    this.expiresAt = expiresAt;
    this.lastActivityReportAt = Date.now();
    this.firstUnauthorizedAt = 0;
    this.backoffUntil = 0;
    this.clearPendingRetry();
    this.startSessionCheck();
    return SessionStatus.Authenticated;
  }

  recordUnauthorized() {
    const now = Date.now();
    this.firstUnauthorizedAt ||= now;
    if (now - this.firstUnauthorizedAt < SIGN_OUT_CONFIRMATION_DELAY_MS) {
      this.scheduleQuickRetry();
      return SessionStatus.Unverified;
    }

    this.endSession();
    return SessionStatus.SignedOut;
  }

  endSession() {
    this.jwtToken = null;
    this.expiresAt = 0;
    this.firstUnauthorizedAt = 0;
    this.clearPendingRetry();
    this.stopSessionCheck();
  }

  // Anything other than a 401 - notably Identity's 503 "refresh failed, try again", other 5xx,
  // network errors and timeouts - says nothing about the session itself, so the last good token
  // is kept.
  recordTransientFailure(reason) {
    console.warn('IdentityAuth: session check failed, will retry', reason);
    this.firstUnauthorizedAt = 0;
    this.backoffUntil = Date.now() + ERROR_BACKOFF_MS;
    return this.statusFromCachedToken();
  }

  statusFromCachedToken() {
    return this.hasUsableToken()
      ? SessionStatus.Authenticated
      : SessionStatus.Unverified;
  }

  hasUsableToken() {
    return this.jwtToken != null && !this.expiresWithin(0);
  }

  expiresWithin(milliseconds) {
    return Date.now() + milliseconds >= this.expiresAt;
  }

  /**
   * How long to wait before asking Identity again after an unverified check.
   */
  retryDelayMs() {
    return Math.max(QUICK_RETRY_DELAY_MS, this.backoffUntil - Date.now());
  }

  /**
   * Renew the session after an API rejected `rejectedToken`.
   * True only when Identity now vouches for a different token: during an Identity outage the
   * cached token still counts as authenticated, but it is the very token that was just refused.
   */
  async renewRejectedToken(rejectedToken) {
    const status = await this.checkSession();
    return (
      status === SessionStatus.Authenticated && this.jwtToken !== rejectedToken
    );
  }

  /**
   * The current token, refreshed first if it is about to expire.
   * Without a session there is nothing to refresh, so anonymous pages never call Identity here.
   */
  async getFreshToken() {
    if (this.jwtToken && this.expiresWithin(TOKEN_REFRESH_LEEWAY_MS)) {
      await this.checkSession();
    }
    return this.jwtToken;
  }

  /**
   * Start periodic session checks, plus a re-check whenever the tab wakes up: browsers throttle
   * timers in background tabs and suspended laptops, so the interval alone cannot be relied on.
   * User activity is tracked only while there is a session to keep alive.
   */
  startSessionCheck() {
    if (this.sessionCheckInterval) return;

    this.clearPendingRetry();

    this.sessionCheckInterval = setInterval(() => {
      void this.checkSession();
    }, SESSION_CHECK_INTERVAL_MS);

    this.addSessionListeners();
  }

  /**
   * Stop periodic session checks
   */
  stopSessionCheck() {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    this.removeSessionListeners();
  }

  addSessionListeners() {
    if (!hasDom()) return;

    // Registered ahead of the wake re-check so that check already reports the user as active.
    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, this.recordInteraction, ACTIVITY_LISTENER_OPTIONS);
    }
    window.addEventListener('focus', this.recordInteraction);
    document.addEventListener('visibilitychange', this.recordInteraction);

    document.addEventListener('visibilitychange', this.recheckOnWake);
    window.addEventListener('online', this.recheckOnWake);
    window.addEventListener('pageshow', this.recheckOnWake);
  }

  removeSessionListeners() {
    if (!hasDom()) return;

    for (const type of ACTIVITY_EVENTS) {
      window.removeEventListener(type, this.recordInteraction, ACTIVITY_LISTENER_OPTIONS);
    }
    window.removeEventListener('focus', this.recordInteraction);
    document.removeEventListener('visibilitychange', this.recordInteraction);

    document.removeEventListener('visibilitychange', this.recheckOnWake);
    window.removeEventListener('online', this.recheckOnWake);
    window.removeEventListener('pageshow', this.recheckOnWake);
  }

  idleMs() {
    return Math.max(0, Math.floor(Date.now() - this.lastInteractionAt));
  }

  recordInteraction = () => {
    if (document.visibilityState === 'hidden') return;

    const now = Date.now();
    if (now - this.lastInteractionAt < ACTIVITY_RECORD_THROTTLE_MS) return;

    this.lastInteractionAt = now;
    this.reportActivityIfOverdue(now);
  };

  // Polling every couple of minutes normally carries the activity, but a throttled or stalled
  // poll must not let an active user be signed out as idle.
  reportActivityIfOverdue(now) {
    if (this.loggedOut || !this.jwtToken) return;
    if (now - this.lastActivityReportAt < ACTIVITY_HEARTBEAT_INTERVAL_MS) return;
    if (now - this.lastHeartbeatAt < ACTIVITY_HEARTBEAT_INTERVAL_MS) return;

    this.lastHeartbeatAt = now;
    void this.checkSession();
  }

  recheckOnWake = () => {
    if (document.visibilityState === 'hidden') return;
    if (Date.now() - this.lastCheckStartedAt < WAKE_RECHECK_THROTTLE_MS) return;

    void this.checkSession();
  };

  clearPendingRetry() {
    if (this.pendingRetryTimeout) {
      clearTimeout(this.pendingRetryTimeout);
      this.pendingRetryTimeout = null;
    }
  }

  scheduleQuickRetry() {
    if (this.pendingRetryTimeout) {
      return;
    }

    this.pendingRetryTimeout = setTimeout(() => {
      this.pendingRetryTimeout = null;
      void this.checkAuth();
    }, QUICK_RETRY_DELAY_MS);
  }

  shouldRedirect() {
    try {
      const last = Number(localStorage.getItem(REDIRECT_STORAGE_KEY));
      if (!Number.isFinite(last)) {
        return true;
      }
      return Date.now() - last >= MIN_REDIRECT_INTERVAL_MS;
    } catch {
      return true;
    }
  }

  markRedirect() {
    try {
      localStorage.setItem(REDIRECT_STORAGE_KEY, Date.now().toString());
    } catch {
      // ignore storage issues
    }
  }

  redirectToLogin(force = false) {
    if (!force && !this.shouldRedirect()) {
      console.warn('IdentityAuth: redirect suppressed due to cooldown');
      this.scheduleRedirectAfterCooldown();
      return;
    }
    this.cancelPendingRedirect();
    this.markRedirect();
    const identityUrl = this.getIdentityUrl();
    window.location.href = `${identityUrl}/login`;
  }

  cancelPendingRedirect() {
    clearTimeout(this.pendingRedirectTimeout);
    this.pendingRedirectTimeout = null;
  }

  scheduleRedirectAfterCooldown() {
    if (this.pendingRedirectTimeout) return;

    this.pendingRedirectTimeout = setTimeout(() => {
      this.pendingRedirectTimeout = null;
      this.redirectToLogin();
    }, MIN_REDIRECT_INTERVAL_MS);
  }

  /**
   * Get current JWT token
   */
  getToken() {
    return this.jwtToken;
  }

  /**
   * Manually refresh the token
   * Useful for proactive token refresh before making important API calls
   */
  async refreshToken() {
    return await this.checkAuth();
  }

  /**
   * Decode JWT token and return claims
   */
  getClaims() {
    if (!this.jwtToken) return null;
    try {
      const base64 = this.jwtToken
        .split('.')[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      return JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        )
      );
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if user has specific role
   */
  hasRole(role) {
    const claims = this.getClaims();
    if (!claims || !claims.claims || !claims.claims.welbee_roles) return false;

    return claims.claims.welbee_roles.some(userRole =>
      userRole.toLowerCase().includes(role.toLowerCase())
    );
  }

  /**
   * Get user information from JWT claims
   */
  getUserInfo() {
    const claims = this.getClaims();
    if (!claims || !claims.claims) return null;

    const welbeeClaims = claims.claims;
    const accessControl = welbeeClaims.welbee_access_control || {};

    // Extract groups and schools from access control
    const groups = accessControl.groups || [];
    const standaloneSchools = accessControl.standalone_school_ids || [];

    // Get all school IDs from groups and standalone schools
    const allSchoolIds = [
      ...groups.flatMap(group => group.school_ids || []),
      ...standaloneSchools,
    ];

    // Determine if user is MAT (has groups)
    const isMat = groups.length > 0;

    // Get primary group ID (first group)
    const primaryGroupId = groups.length > 0 ? groups[0].group_id : null;

    // Get primary school ID (first school)
    const primarySchoolId = allSchoolIds.length > 0 ? allSchoolIds[0] : null;

    const allSchools = groups.length > 0 ? groups.some(group => group.all_schools === true) : false;

    return {
      // Basic user info
      userId: welbeeClaims.welbee_internal_user_id || '',
      email: welbeeClaims.welbee_email || '',
      name: welbeeClaims.welbee_name || '',
      roles: welbeeClaims.welbee_roles || [],
      userType: welbeeClaims.welbee_user_type || '',

      // Organization info
      groups: groups.map(group => ({
        id: group.group_id,
        allSchools: group.all_schools,
        schoolIds: group.school_ids || [],
      })),
      schools: allSchoolIds,
      isMat: isMat,
      matGroupId: primaryGroupId,
      schoolId: primarySchoolId,
      allSchools: allSchools,

      // Access control info
      accessControl: {
        groups: groups,
        standaloneSchoolIds: standaloneSchools,
        accessOneSchoolOnly: accessControl.access_one_school_only || false,
        accessMultipleCustomers:
          accessControl.access_multiple_customers || false,
      },

      // Token metadata
      claimsVersion: welbeeClaims.welbee_claims_version || '1.0',
      issuedAt: welbeeClaims.welbee_issued_at,
      expiresAt: welbeeClaims.welbee_expires_at,
    };
  }

  /**
   * Check if current session is expired
   */
  isSessionExpired() {
    if (!this.expiresAt) return true;
    return Date.now() >= this.expiresAt;
  }

  /**
   * Check if user is a MAT (Multi-Academy Trust) user
   */
  isMatUser() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.isMat : false;
  }

  /**
   * Get user's primary group ID
   */
  getPrimaryGroupId() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.matGroupId : null;
  }

  /**
   * Get user's primary school ID
   */
  getPrimarySchoolId() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.schoolId : null;
  }

  /**
   * Get all school IDs user has access to
   */
  getAllSchoolIds() {
    const userInfo = this.getUserInfo();
    return userInfo ? userInfo.schools : [];
  }

  /**
   * Check if user has access to a specific school
   */
  hasAccessToSchool(schoolId) {
    const schoolIds = this.getAllSchoolIds();
    return schoolIds.includes(schoolId);
  }

  /**
   * Check if user has access to all schools in a group
   */
  hasAccessToAllSchools() {
    const userInfo = this.getUserInfo();
    if (!userInfo || !userInfo.groups.length) return false;

    return userInfo.groups.some(group => group.allSchools);
  }

  /**
   * Logout user and redirect to Identity app
   */
  async logout() {
    this.loggedOut = true;
    this.endSession();
    this.cancelPendingRedirect();

    try {
      const identityUrl = this.getIdentityUrl();

      await fetch(`${identityUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });

      this.redirectToLogin(true);
    } catch (error) {
      this.redirectToLogin(true);
    }
  }

  /**
   * Make authenticated API calls with JWT token
   */
  async fetchWithAuth(url, options = {}) {
    if (!this.jwtToken) {
      throw new Error('Not authenticated');
    }

    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${this.jwtToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }
}

// Export singleton instance
export const identityAuth = new IdentityAuth();
